import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import { SchoolClassSelection } from "../../../shared-components/workflows/SchoolClassSelection";
import { checkUserHasRole, getSchoolsForUser } from "../../auth/Utils";

export const AssignmentFromTemplateDialog = ({
  open,
  setOpen,
  assignmentTemplate,
}) => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { user } = useAuthenticationStore();

  const schools = getSchoolsForUser();

  const createGroupWithName = searchParams.get("createGroupWithName");

  const createAssignmentGroup = Boolean(createGroupWithName);

  const [assignmentName, setAssignmentName] = useState(
    createGroupWithName || "",
  );

  const [dueDate, setDueDate] = useState(dayjs());

  const [selectedClasses, setSelectedClasses] = useState([]);

  useEffect(() => {
    if (createGroupWithName) {
      setAssignmentName(createGroupWithName);
    }
  }, [createGroupWithName]);

  useEffect(() => {
    setSelectedClasses([]);
  }, [assignmentTemplate?.id]);

  const handleCreateAssignment = async () => {
    if (!assignmentName || !dueDate || selectedClasses.length === 0) {
      return;
    }

    try {
      const response = await ApiClient.assignment.createAssignmentFromTemplate({
        assignmentTemplate: assignmentTemplate.id,
        name: assignmentName,
        dueDate: dueDate.format("YYYY-MM-DD"),
        schoolClasses: selectedClasses.map((schoolClass) => schoolClass.id),
        createAssignmentGroup,
      });

      if (!response?.success) {
        enqueueSnackbar(response?.message || "Failed to create assignment", {
          variant: "error",
        });
        return;
      }

      enqueueSnackbar("Successfully created assignment!", {
        variant: "success",
        autoHideDuration: 3000,
      });

      setOpen(false);

      if (
        checkUserHasRole({
          user,
          role: "teacher",
        })
      ) {
        if (response.assignments.length === 1) {
          navigate(`/assignments/view/${response.assignments[0].id}`);
        } else {
          navigate("/assignments");
        }
      }
    } catch (error) {
      console.error("Failed to create assignment", error);

      enqueueSnackbar("Failed to create assignment", {
        variant: "error",
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      slotProps={{
        paper: {
          component: "form",
          sx: {
            backgroundImage: "none",
          },
        },
      }}
    >
      <DialogTitle>Create assignment</DialogTitle>

      <DialogContent>
        <Stack direction="column" spacing={2} maxWidth={400} sx={{ mt: 2 }}>
          <TextField
            label="Assignment Name"
            variant="outlined"
            size="small"
            value={assignmentName}
            onChange={(event) => setAssignmentName(event.target.value)}
            disabled={createAssignmentGroup}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Due date"
              format="DD/MM/YYYY"
              value={dueDate}
              minDate={dayjs()}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    "& .MuiInputAdornment-root": {
                      border: "none",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Stack>

        <Box sx={{ mt: 4 }}>
          <SchoolClassSelection
            selectedClasses={selectedClasses}
            onSelectionChange={setSelectedClasses}
            assignmentTemplate={assignmentTemplate}
            schools={schools}
            userRole={user.info.currentRole}
            enabled={open}
            isTeacher={checkUserHasRole({
              user,
              role: "teacher",
            })}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>

        <Button
          disabled={selectedClasses.length === 0 || !assignmentName || !dueDate}
          onClick={handleCreateAssignment}
          variant="contained"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
